/* File: frontend/src/pages/UserProfileAdmin.js */
/* Version: 1.7 (quick-260915-d2d — зашитый цвет убран, экран на общем слое) */
/* Date: 2026-09-15 */
/* Description: 
   - [UI]: Redesigned to match "Case Management" card style.
   - [LOGIC]: Preserved Google Calendar connection functionality.
*/

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import apiService from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { User, Mail, Phone, Calendar, Shield, Activity, RefreshCw, CheckCircle, Bot } from 'lucide-react';
// 🔴 ОБЩИЙ СЛОЙ ОФОРМЛЕНИЯ: `Sostoyanie` — единственный держатель того, как
//    экран говорит «грузим» и «не нашли».
import { Sostoyanie } from '../components/FitenkoUzor';
import './UserProfileAdmin.css';

const UserProfileAdmin = () => {
    const { user, loading: authLoading, updateUser } = useAuth();
    const location = useLocation();
    const [isConnecting, setIsConnecting] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [aiPersona, setAiPersona] = useState(user?.ai_persona || 'colleague');

    // Обновляем состояние, если объект user изменился
    useEffect(() => {
        if (user && user.ai_persona) {
            setAiPersona(user.ai_persona);
        }
    }, [user]);

    // Обработка возврата от Google
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const authStatus = queryParams.get('google_auth');
        const detail = queryParams.get('detail');

        if (authStatus === 'success') {
            toast.success('Google Calendar successfully connected!');
            window.history.replaceState({}, document.title, location.pathname);
            window.location.reload();
        } else if (authStatus === 'error') {
            toast.error(`Connection failed: ${detail || 'Unknown error'}`);
            window.history.replaceState({}, document.title, location.pathname);
        }
    }, [location]);

    // Логика подключения
    const handleConnectGoogleCalendar = async () => {
        setIsConnecting(true);
        const toastId = toast.loading('Redirecting to Google...');
        try {
            const response = await apiService.getGoogleAuthUrl();
            if (response && response.authorization_url) {
                window.location.href = response.authorization_url;
            } else {
                throw new Error('No auth URL received.');
            }
        } catch (error) {
            toast.error(`Error: ${error.message}`, { id: toastId });
            setIsConnecting(false);
        }
    };

    // Логика отключения
    const handleDisconnect = async () => {
        if (!window.confirm('Disconnect Google Calendar?')) return;
        
        setIsDisconnecting(true);
        const toastId = toast.loading('Disconnecting...');
        try {
            const updatedUser = await apiService.disconnectGoogleCalendar();
            updateUser(updatedUser); 
            toast.success('Disconnected successfully.', { id: toastId });
        } catch (error) {
            toast.error(`Failed: ${error.message}`, { id: toastId });
        } finally {
            setIsDisconnecting(false);
        }
    };

    // Логика обновления персоны
    const handlePersonaChange = async (newPersona) => {
        setAiPersona(newPersona);
        const toastId = toast.loading('Updating AI Assistant Persona...');
        try {
            const updatedUser = await apiService.updateMyPersona(newPersona);
            updateUser(updatedUser); 
            toast.success('Persona updated successfully.', { id: toastId });
        } catch (error) {
            toast.error(`Failed: ${error.message}`, { id: toastId });
            // revert back on error
            if (user && user.ai_persona) {
                setAiPersona(user.ai_persona);
            }
        }
    };

    if (authLoading) return <div className="loading-state"><Sostoyanie tekst="Loading profile…" /></div>;
    if (!user) return <div className="error-state"><Sostoyanie tekst="User not found." /></div>;

    const isIntegrated = user.google_calendar_integrated;

    // Phase 07.1 Wave 2: page wrapper tokenized — bg-background + text-foreground tone
    // MLG (white/slate) and Fitenko (cream/ink) via Wave 1 cascade.
    return (
        <div className="user-profile-admin-container bg-background text-foreground">
            {/* Header Section */}
            <div className="page-header-flex">
                <div className="header-titles">
                    <h1 className="page-title text-foreground">User Profile</h1>
                    <p className="page-subtitle text-muted-foreground">Manage your personal settings and integrations.</p>
                </div>
                <div className="header-badges">
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                </div>
            </div>

            {/* Content Grid */}
            <div className="profile-grid">

                {/* CARD 1: User Details — bg-card maps to white (MLG) / cream-deep (Fitenko) */}
                <div className="platinum-card details-card bg-card text-card-foreground">
                    <div className="card-header">
                        <User size={20} className="card-icon"/>
                        <h2>Personal Information</h2>
                    </div>
                    <div className="card-content">
                        <div className="info-row">
                            <label><User size={16}/> Full Name</label>
                            <span className="info-value">{user.first_name} {user.last_name}</span>
                        </div>
                        <div className="info-row">
                            <label><Mail size={16}/> Email</label>
                            <span className="info-value">{user.email}</span>
                        </div>
                        <div className="info-row">
                            <label><Shield size={16}/> Role</label>
                            <span className="role-pill">
                                {user.roles?.[0]?.name?.replace('_', ' ') || 'User'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* CARD 2: Integrations — surface tokenized; Calendar Connect button uses
                    bg-primary (gold on Fitenko, blue on MLG) with uppercase letter-spacing for
                    editorial button language per CONTEXT.md typography spec. */}
                <div className="platinum-card integrations-card bg-card text-card-foreground">
                    <div className="card-header">
                        <Activity size={20} className="card-icon"/>
                        <h2>Integrations</h2>
                    </div>
                    <div className="card-content">
                        <div className="integration-item">
                            <div className="integration-left">
                                <div className="integration-icon-bg">
                                    {/* Цвет значка приходит КАСКАДОМ от .integration-icon-bg (currentColor),
                                        а не зашитым литералом в разметке. */}
                                    <Calendar size={24} color="currentColor" />
                                </div>
                                <div className="integration-info">
                                    <h3>Google Calendar</h3>
                                    <p>Sync appointments with AI Paralegal.</p>
                                </div>
                            </div>

                            <div className="integration-right">
                                {isIntegrated ? (
                                    <button
                                        className="btn-outline-danger"
                                        onClick={handleDisconnect}
                                        disabled={isDisconnecting}
                                    >
                                        {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                                    </button>
                                ) : (
                                    <button
                                        className="btn-dark bg-primary text-primary-foreground uppercase tracking-wider"
                                        onClick={handleConnectGoogleCalendar}
                                        disabled={isConnecting}
                                    >
                                        {isConnecting ? 'Connecting...' : 'Connect'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {isIntegrated && (
                            <div className="integration-status-bar">
                                <CheckCircle size={16} /> Connected as {user.email}
                            </div>
                        )}
                    </div>
                </div>

                {/* CARD 3: AI Customization — surface and form field tokenized; select uses
                    bg-popover (paper) + border-input + text-foreground via shadcn tokens. */}
                {user?.email === 'afitbk@gmail.com' && (
                    <div className="platinum-card ai-customization-card bg-card text-card-foreground" style={{ gridColumn: '1 / -1' }}>
                        <div className="card-header">
                            <Bot size={20} className="card-icon"/>
                            <h2>AI Assistant Persona</h2>
                        </div>
                        <div className="card-content">
                            <div className="info-row" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <p className="text-muted-foreground" style={{ fontSize: '0.9rem', margin: 0 }}>
                                    Customize how your personal AI Assistant speaks to you in direct messages (Global Chat Widget).
                                </p>
                                <select
                                    value={aiPersona}
                                    onChange={(e) => handlePersonaChange(e.target.value)}
                                    className="bg-popover text-foreground border border-input"
                                    style={{
                                        padding: '10px',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        outline: 'none',
                                        cursor: 'pointer',
                                        width: '100%',
                                        maxWidth: '400px'
                                    }}
                                >
                                    <option value="colleague">Colleague (Professional & Respectful)</option>
                                    <option value="friend">Friend (Casual & Warm)</option>
                                    <option value="close_friend">Close Friend (Empathetic & Very Open)</option>
                                    <option value="romantic">Romantic (Flirtatious & Devoted)</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default UserProfileAdmin;